import { useState } from "react";
import { format } from "date-fns";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { DataTable } from "@/components/ui/data-table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

interface LoginHistoryModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface LoginRecord {
  id: string;
  login_timestamp: string;
  ip_address: string;
  user_agent: string;
  success: boolean;
  location: string;
  device_type: string;
}

export const LoginHistoryModal = ({ open, onOpenChange }: LoginHistoryModalProps) => {
  const { data: loginHistory, isLoading } = useQuery({
    queryKey: ["login-history"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("login_history")
        .select("*")
        .order("login_timestamp", { ascending: false });

      if (error) throw error;
      return data as LoginRecord[];
    },
  });

  const columns: {
    header: string;
    accessorKey: keyof LoginRecord;
    cell?: ({ row }: { row: { original: LoginRecord } }) => React.ReactNode;
  }[] = [
    {
      header: "Date",
      accessorKey: "login_timestamp",
      cell: ({ row }) => format(new Date(row.original.login_timestamp), "PPpp"),
    },
    {
      header: "Status",
      accessorKey: "success",
      cell: ({ row }) => (
        <span className={row.original.success ? "text-green-600" : "text-red-600"}>
          {row.original.success ? "Success" : "Failed"}
        </span>
      ),
    },
    {
      header: "IP Address",
      accessorKey: "ip_address",
    },
    {
      header: "Device",
      accessorKey: "device_type",
    },
    {
      header: "Location",
      accessorKey: "location",
    },
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl">
        <DialogHeader>
          <DialogTitle>Login History</DialogTitle>
        </DialogHeader>
        <div className="py-4">
          {isLoading ? (
            <div className="flex items-center justify-center p-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900" />
            </div>
          ) : (
            <DataTable
              data={loginHistory || []}
              columns={columns}
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};